/**
 * PortalTopbar — topbar for the (portal) layout.
 * Simpler: logo, user info, logout. No sidebar toggle.
 */

import { LogOut } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { useAuthStore } from '@/stores/auth-store';
import { useLogoutMutation } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { PORTAL_NAV } from '@/components/layout/PortalSidebar';
import PortalHelpLink from '@/components/layout/PortalHelpLink';

export default function PortalTopbar() {
  const user = useAuthStore((s) => s.user);
  const logoutMutation = useLogoutMutation();

  return (
    <header className="h-14 flex items-center justify-between px-6 border-b border-border bg-card">
      {/* Wordmark */}
      <div className="flex items-center gap-3">
        <span className="font-bold text-primary text-sm">NIRC SCO</span>
        <span className="text-muted-foreground text-sm hidden sm:inline">Faculty &amp; Vendor Portal</span>
      </div>

      {/* Portal navigation links */}
      {user && (
        <nav className="hidden sm:flex items-center gap-1" aria-label="Portal navigation">
          {PORTAL_NAV.map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                )
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
      )}

      {/* User actions */}
      {user && (
        <div className="flex items-center gap-3">
          <PortalHelpLink />
          <span className="text-sm text-muted-foreground hidden sm:inline">{user.full_name}</span>
          <button
            type="button"
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
            aria-label="Log out"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-60"
          >
            <LogOut size={15} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      )}
    </header>
  );
}
